import validator from "validator";
const SOCIAL_MEDIA_PATTERNS = {
    instagram: {
        hosts: ["instagram.com", "www.instagram.com"],
        pathPatterns: [
            /^\/[a-z0-9_.]{1,30}\/?$/i,
            /^\/p\/[A-Za-z0-9_-]+\/?$/i,
            /^\/reel\/[A-Za-z0-9_-]+\/?$/i,
            /^\/stories\/[A-Za-z0-9_.-]+\/[0-9]+\/?$/i,
        ],
        contentUnavailablePatterns: [
            /sorry, this page isn['’]t available/i,
            /cette page n['’]est malheureusement pas disponible/i,
        ],
    },
    tiktok: {
        hosts: ["tiktok.com", "www.tiktok.com"],
        pathPatterns: [/^\/@[A-Za-z0-9_.]{2,24}\/?$/],
    },
    linkedin: {
        hosts: ["linkedin.com", "www.linkedin.com"],
        pathPatterns: [
            /^\/in\/[A-Za-z0-9À-ÖØ-öø-ÿ\-_%]{3,100}\/?$/u,
            /^\/company\/[A-Za-z0-9À-ÖØ-öø-ÿ\-_%]{2,100}\/?$/u,
            /^\/school\/[A-Za-z0-9À-ÖØ-öø-ÿ\-_%]{2,100}\/?$/u,
            /^\/groups\/[0-9]{4,12}\/?$/,
        ],
    },
    youtube: {
        hosts: ["youtube.com", "www.youtube.com", "youtu.be"],
        pathPatterns: [
            /^\/channel\/[A-Za-z0-9_-]{5,80}\/?$/,
            /^\/c\/[A-Za-z0-9_-]{1,80}\/?$/,
            /^\/user\/[A-Za-z0-9_-]{1,80}\/?$/,
            /^\/@[A-Za-z0-9_.-]{1,80}\/?$/,
        ],
    },
};
function ensureProtocol(url) {
    if (/^https?:\/\//i.test(url)) {
        return url;
    }
    if (/^www\./i.test(url)) {
        return `https://${url}`;
    }
    return `https://${url}`;
}
export function normalizeUrl(rawInput) {
    const value = rawInput.trim();
    if (!value) {
        return "";
    }
    const withProtocol = ensureProtocol(value);
    try {
        const url = new URL(withProtocol);
        url.hostname = url.hostname.toLowerCase();
        if ((url.protocol === "https:" && url.port === "443") ||
            (url.protocol === "http:" && url.port === "80")) {
            url.port = "";
        }
        if ((url.pathname === "/" || url.pathname === "") &&
            !url.search &&
            !url.hash) {
            url.pathname = "";
        }
        else if (url.pathname) {
            url.pathname = url.pathname.replace(/\/{2,}/g, "/");
        }
        return url.toString();
    }
    catch {
        return withProtocol;
    }
}
// Common TLDs - keeping it simple with the most used ones
const COMMON_TLDS = new Set([
    // Generic TLDs
    "com",
    "org",
    "net",
    "edu",
    "gov",
    "io",
    "co",
    "app",
    "dev",
    "me",
    "tv",
    "info",
    "biz",
    "name",
    "pro",
    "xyz",
    "online",
    "site",
    "store",
    "tech",
    "website",
    "blog",
    "shop",
    "cloud",
    "ai",
    "digital",
    "studio",
    "agency",
    // Country codes
    "fr",
    "uk",
    "de",
    "us",
    "ca",
    "au",
    "jp",
    "cn",
    "in",
    "br",
    "es",
    "it",
    "nl",
    "be",
    "ch",
    "se",
    "no",
    "dk",
    "fi",
    "pl",
    "pt",
    "ru",
    "kr",
    "mx",
]);
export function isValidDomain(domain) {
    if (!domain) {
        return false;
    }
    const normalized = domain.trim().toLowerCase();
    if (!normalized || normalized.endsWith(".")) {
        return false;
    }
    // First check with validator
    if (!validator.isFQDN(normalized, {
        require_tld: true,
        allow_underscores: false,
    })) {
        return false;
    }
    // Then check if TLD is valid
    const parts = normalized.split(".");
    if (parts.length < 2) {
        return false;
    }
    const tld = parts[parts.length - 1];
    // Simple check: TLD should be in our list of common TLDs
    return COMMON_TLDS.has(tld);
}
export function isValidUrlFormat(url) {
    const normalized = normalizeUrl(url);
    return validator.isURL(normalized, {
        require_protocol: true,
        protocols: ["http", "https"],
        require_valid_protocol: true,
        allow_underscores: false,
        allow_trailing_dot: false,
    });
}
function matchPlatformPattern(platform, pathname) {
    const pattern = SOCIAL_MEDIA_PATTERNS[platform];
    if (!pattern.pathPatterns || pattern.pathPatterns.length === 0) {
        return true;
    }
    return pattern.pathPatterns.some((regex) => regex.test(pathname));
}
export function detectSocialPlatform(url) {
    try {
        const parsed = new URL(normalizeUrl(url));
        const host = parsed.hostname.toLowerCase();
        for (const platform of Object.keys(SOCIAL_MEDIA_PATTERNS)) {
            const { hosts } = SOCIAL_MEDIA_PATTERNS[platform];
            if (hosts.includes(host)) {
                if (matchPlatformPattern(platform, parsed.pathname)) {
                    return platform;
                }
            }
        }
        return null;
    }
    catch {
        return null;
    }
}
export function validateSocialMediaUrl(url) {
    const platform = detectSocialPlatform(url);
    if (!platform) {
        return false;
    }
    try {
        const parsed = new URL(normalizeUrl(url));
        return matchPlatformPattern(platform, parsed.pathname);
    }
    catch {
        return false;
    }
}
export function categorizeUrl(url) {
    const normalized = normalizeUrl(url);
    const platform = detectSocialPlatform(normalized);
    if (platform) {
        return platform;
    }
    if (isValidUrlFormat(normalized)) {
        return "website";
    }
    return "unknown";
}
export function buildUrlValidationSummary(url) {
    const normalized = normalizeUrl(url);
    const isFormatValid = isValidUrlFormat(normalized);
    const platform = detectSocialPlatform(normalized);
    const domain = (() => {
        try {
            return new URL(normalized).hostname || null;
        }
        catch {
            return null;
        }
    })();
    return {
        normalizedUrl: normalized,
        isFormatValid,
        platform,
        isDomainValid: domain ? isValidDomain(domain) : false,
    };
}
export function isLikelyReachableUrl(url) {
    try {
        const parsed = new URL(normalizeUrl(url));
        if (!isValidDomain(parsed.hostname)) {
            return false;
        }
        if (/^(localhost|127\.|10\.|172\.(1[6-9]|2\d|3[01])\.|192\.168\.)/.test(parsed.hostname)) {
            return false;
        }
        return true;
    }
    catch {
        return false;
    }
}
export function getPlatformDefinition(platform) {
    return SOCIAL_MEDIA_PATTERNS[platform];
}
export function listSupportedPlatforms() {
    return Object.keys(SOCIAL_MEDIA_PATTERNS);
}
export function isPlatformContentUnavailable(platform, content) {
    const definition = SOCIAL_MEDIA_PATTERNS[platform];
    const indicators = definition.contentUnavailablePatterns;
    if (!indicators || indicators.length === 0) {
        return false;
    }
    return indicators.some((pattern) => pattern.test(content));
}
export function requiresContentVerification(platform) {
    const definition = SOCIAL_MEDIA_PATTERNS[platform];
    return Boolean(definition.contentUnavailablePatterns?.length);
}
